import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { RevealLines, Reveal, Marquee, Faq, ContactLinks, LogoMark, ServiceTiles, Steps, SoftLanding, Partners } from "../components/shared";
import { HERO, ABOUT, STEVEN, APPROACH, HOURS, FORMATS, CONTACT, ACKNOWLEDGEMENT, SCRIPT } from "../content";

const Eyebrow = ({ children, light }) => <p className={`font-dmsans text-xs uppercase tracking-[0.3em] ${light ? "text-[#F6F2E9]" : "text-[#5E6B57]"}`}>{children}</p>;

const Nav = () => (
  <header className="fixed top-4 inset-x-4 sm:inset-x-8 z-40">
    <div className="max-w-[1300px] mx-auto flex items-center justify-between rounded-full bg-[#F6F2E9]/70 backdrop-blur-xl border border-white/60 pl-2 pr-2 py-2 shadow-[0_8px_30px_rgba(27,59,43,0.08)]">
      <a href="#top" data-testid="nav-logo" className="flex items-center gap-3"><LogoMark className="w-10 h-10" /><span className="font-instrument text-xl text-[#3F4A3C] hidden sm:inline">Inner You Development</span></a>
      <nav className="hidden md:flex gap-8 font-dmsans text-sm text-[#3F4A3C]">
        {["Care", "Services", "Visit", "FAQ"].map((l) => <a key={l} href={`#${l.toLowerCase()}`} data-testid={`nav-${l.toLowerCase()}`} className="opacity-80 hover:opacity-100 transition-opacity">{l}</a>)}
        <Link to="/team" data-testid="nav-team" className="opacity-80 hover:opacity-100 transition-opacity">Team</Link>
      </nav>
      <a href={CONTACT.phoneHref} data-testid="nav-call-btn" className="rounded-full bg-[#3F4A3C] text-[#F6F2E9] font-dmsans text-sm px-5 py-2.5 hover:bg-[#56654F] transition-colors">Book a session</a>
    </div>
  </header>
);

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const scale = useTransform(scrollYProgress, [0, 1], [1.05, 1.25]);
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);
  return (
    <section id="top" ref={ref} className="relative h-[100svh] min-h-[640px] overflow-hidden" data-testid="hero">
      <motion.img initial={{ scale: 1.12, opacity: 0 }} animate={{ scale: 1.02, opacity: 1 }} transition={{ duration: 2.2, ease: [0.22, 1, 0.36, 1] }} style={{ scale, y }} src="/images/leaf-hero-wide.jpg" alt="Soft sage leaves on cream" className="absolute inset-0 w-full h-full object-cover object-[70%_center]" data-testid="hero-image" />
      <div className="absolute inset-0 bg-gradient-to-r from-[#F6F2E9] via-[#F6F2E9]/80 to-transparent lg:via-[#F6F2E9]/20" />
      <motion.div style={{ opacity: fade }} className="relative h-full max-w-[1300px] mx-auto px-6 sm:px-10 flex flex-col justify-end pb-24 sm:pb-28">
        <Eyebrow>{HERO.eyebrow}</Eyebrow>
        <RevealLines lines={HERO.lines} delay={0.6} className="mt-6 font-instrument text-[#3F4A3C] text-6xl sm:text-8xl lg:text-[8.5rem] leading-[0.9] tracking-tight" emIndex={2} emClass="italic text-[#5F7C8A]" />
        <Reveal delay={1.1} className="mt-10 flex flex-col sm:flex-row sm:items-end justify-between gap-8">
          <p className="font-dmsans text-base sm:text-lg text-[#4A4F45] max-w-md leading-relaxed">{HERO.sub}</p>
          <a href={CONTACT.phoneHref} data-testid="hero-book-btn" className="group inline-flex items-center gap-3 self-start rounded-full bg-[#3F4A3C] px-7 py-4 font-dmsans text-sm text-[#F6F2E9] hover:bg-[#5F7C8A] transition-colors duration-500">
            Call {CONTACT.phone} <ArrowUpRight className="w-4 h-4 transition-transform group-hover:rotate-45 duration-500" />
          </a>
        </Reveal>
      </motion.div>
    </section>
  );
};

const Care = () => (
  <section id="care" className="relative py-28 sm:py-40 overflow-hidden" data-testid="about-section">
    <div className="absolute -top-40 -right-40 w-[520px] h-[520px] rounded-full bg-[#F5E3CF]/50 blur-3xl" />
    <div className="relative max-w-[1300px] mx-auto px-6 sm:px-10">
      <Reveal><Eyebrow>Our care</Eyebrow></Reveal>
      <Reveal delay={0.1}><h2 className="mt-8 font-instrument text-4xl sm:text-6xl lg:text-7xl leading-[1.05] text-[#3F4A3C] max-w-5xl">{ABOUT.title.replace("meaningful change.", "")}<em className="text-[#6B7A63]">meaningful change.</em></h2></Reveal>
      <div className="mt-14 grid md:grid-cols-2 gap-10 max-w-4xl font-dmsans text-base sm:text-lg leading-relaxed text-[#4A4F45]">
        {ABOUT.body.map((p, i) => <Reveal key={i} delay={0.1 * i}><p>{p}</p></Reveal>)}
      </div>
      <div className="mt-24 grid md:grid-cols-3 gap-5">
        {APPROACH.map((a, i) => (
          <Reveal key={a.n} delay={i * 0.1}>
            <motion.div whileHover={{ y: -6 }} transition={{ duration: 0.4 }} className="h-full rounded-[28px] bg-white/70 backdrop-blur-xl border border-[#3F4A3C]/10 p-8 sm:p-10 shadow-[0_20px_60px_-30px_rgba(27,59,43,0.25)]" data-testid={`approach-${a.title.toLowerCase()}`}>
              <span className="font-instrument italic text-5xl text-[#C99A4E]">{a.n}</span>
              <h3 className="mt-10 font-instrument text-3xl text-[#3F4A3C]">{a.title}</h3>
              <p className="mt-3 font-dmsans text-sm sm:text-base text-[#4A4F45] leading-relaxed">{a.text}</p>
            </motion.div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const Quote = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-6%", "6%"]);
  return (
    <section ref={ref} className="relative py-28 sm:py-40 overflow-hidden bg-[#5F7C8A]" data-testid="steven-section">
      <div className="relative max-w-[1300px] mx-auto px-6 sm:px-10 grid lg:grid-cols-12 gap-12 items-center">
        <Reveal className="lg:col-span-7 text-[#F6F2E9]">
          <Eyebrow light>Led by {STEVEN.name}</Eyebrow>
          <p className="mt-8 font-instrument text-3xl sm:text-5xl leading-tight">“We believe in your ability to <em>heal, grow and thrive.</em>”</p>
          <p className="mt-8 font-dmsans text-base text-[#F2F0EA] leading-relaxed">{STEVEN.body}</p>
          <p className="mt-6 font-dmsans text-xs uppercase tracking-[0.25em] text-[#DCE6EC]">{STEVEN.role} · {STEVEN.years} years</p>
        </Reveal>
        <div className="lg:col-span-5 overflow-hidden rounded-t-[999px] rounded-b-[28px]">
          <motion.img style={{ y }} src="/images/leaf-closeup.jpg" alt="Close-up of a soft sage leaf" className="w-full aspect-[4/5] object-cover scale-110" />
        </div>
      </div>
    </section>
  );
};

const Services = () => (
  <section id="services" className="py-28 sm:py-40" data-testid="services-section">
    <div className="max-w-[1300px] mx-auto px-6 sm:px-10">
      <Reveal className="flex flex-col md:flex-row md:items-end justify-between gap-6">
        <div><Eyebrow>Services</Eyebrow><h2 className="mt-6 font-instrument text-5xl sm:text-7xl text-[#3F4A3C]">Ways we <em>walk with you.</em></h2></div>
        <div className="flex flex-wrap gap-2">{FORMATS.map((f) => <span key={f} className="rounded-full bg-[#E3E9E4] px-4 py-2 font-dmsans text-xs text-[#3F4A3C]">{f}</span>)}</div>
      </Reveal>
      <div className="mt-16 relative rounded-[36px] overflow-hidden p-4 sm:p-8">
        <img src="/images/leaf-stem-low.jpg" alt="Sage leaf stem on cream" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-[#DCE6EC]/25" />
        <ServiceTiles className="relative" tileClass="rounded-[24px] bg-[#F9F6EF]/80 backdrop-blur-md hover:bg-[#F9F6EF]" iconClass="text-[#56654F]" titleClass="font-instrument text-2xl text-[#3F4A3C]" textClass="font-dmsans text-sm text-[#4A4F45] leading-relaxed" />
      </div>
      <Reveal className="mt-28"><Eyebrow>Making a referral</Eyebrow><h3 className="mt-6 font-instrument text-4xl sm:text-6xl text-[#3F4A3C]">{SCRIPT.replace("support most.", "")}<em className="text-[#6B7A63]">support most.</em></h3></Reveal>
      <Steps className="mt-14" circleClass="bg-[#DCE6EC] text-[#3F4A3C]" numClass="bg-[#3F4A3C] text-[#F6F2E9] font-dmsans" lineClass="bg-[#3F4A3C]/20" titleClass="font-instrument text-2xl text-[#3F4A3C]" textClass="font-dmsans text-sm text-[#4A4F45] leading-relaxed" />
    </div>
  </section>
);

const Visit = () => (
  <section id="visit" className="px-4 sm:px-8" data-testid="visit-section">
    <div className="relative max-w-[1300px] mx-auto rounded-[36px] overflow-hidden bg-[#DCE6EC]">
      <div className="relative grid lg:grid-cols-2 gap-6 p-5 sm:p-10 lg:p-16">
        <Reveal className="rounded-[28px] bg-[#F6F2E9]/85 backdrop-blur-xl p-8 sm:p-10">
          <Eyebrow>Visit</Eyebrow>
          <h2 className="mt-6 font-instrument text-4xl sm:text-5xl text-[#3F4A3C]">Upper Mount Gravatt</h2>
          <p className="mt-4 font-dmsans text-[#4A4F45]">{CONTACT.address}</p>
          <ContactLinks prefix="visit" className="mt-8 flex flex-col gap-4 font-dmsans text-[#3F4A3C]" linkClass="hover:text-[#56654F]" />
        </Reveal>
        <Reveal delay={0.1} className="rounded-[28px] bg-[#F6F2E9]/85 backdrop-blur-xl p-8 sm:p-10">
          <Eyebrow>Hours</Eyebrow>
          <ul className="mt-6" data-testid="hours-list">
            {HOURS.map((h) => <li key={h.day} className="flex justify-between gap-4 py-5 border-b border-[#3F4A3C]/10 last:border-0 font-dmsans"><span className="text-[#4A4F45]">{h.day}</span><span className="text-[#3F4A3C] font-medium text-right">{h.time}</span></li>)}
          </ul>
        </Reveal>
      </div>
    </div>
  </section>
);

const FaqBlock = () => (
  <section id="faq" className="py-28 sm:py-40" data-testid="faq-section">
    <div className="max-w-[900px] mx-auto px-6 sm:px-10">
      <Reveal className="text-center"><Eyebrow>Questions</Eyebrow><h2 className="mt-6 font-instrument text-5xl sm:text-6xl text-[#3F4A3C]">Gentle <em>answers.</em></h2></Reveal>
      <Reveal className="mt-14" delay={0.1}><Faq itemClass="border-[#3F4A3C]/10" triggerClass="font-dmsans text-base sm:text-lg text-[#3F4A3C] py-6" contentClass="font-dmsans text-base text-[#4A4F45] leading-relaxed" /></Reveal>
    </div>
  </section>
);

const Footer = () => (
  <footer className="bg-[#3A4436] text-[#F6F2E9] pt-24 pb-28 rounded-t-[40px]" data-testid="footer">
    <div className="max-w-[1300px] mx-auto px-6 sm:px-10">
      <Reveal><h2 className="font-instrument text-5xl sm:text-8xl leading-none">Let's begin, <em className="text-[#F3DCA8]">gently.</em></h2></Reveal>
      <ContactLinks prefix="footer" className="mt-12 flex flex-col sm:flex-row gap-6 sm:gap-12 font-dmsans text-lg" linkClass="hover:text-[#DCE6EC] transition-colors" />
      <div className="mt-20 pt-10 border-t border-white/15 grid md:grid-cols-3 gap-8">
        <Eyebrow light>Acknowledgement of Country</Eyebrow>
        <p className="md:col-span-2 font-dmsans text-sm leading-relaxed text-[#EEEADF]" data-testid="acknowledgement">{ACKNOWLEDGEMENT}</p>
      </div>
      <p className="mt-14 font-dmsans text-xs text-[#D9DFCF]">© {new Date().getFullYear()} Inner You Development Pty Ltd</p>
    </div>
  </footer>
);

export default function Canopy() {
  return (
    <main className="bg-[#F6F2E9] text-[#4A4F45] overflow-x-hidden" data-testid="canopy-page">
      <Nav />
      <Hero />
      <Marquee className="py-8 bg-[#E3E9E4]" itemClass="font-instrument italic text-4xl sm:text-6xl text-[#3F4A3C]" dotClass="text-[#D9A85A] text-lg" />
      <Care />
      <SoftLanding serif="font-instrument" sans="font-dmsans" />
      <Quote />
      <Services />
      <Partners serif="font-instrument" sans="font-dmsans" />
      <Visit />
      <FaqBlock />
      <Footer />
    </main>
  );
}
